import React, { useEffect, useRef, useState } from 'react';
import { createInvitation, sendInvitationEmail } from '../../services/Authentication';
import { OrganizationAdminSummary } from '../../services/AdminOrganizations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Check, Copy, MailCheck, RefreshCw } from 'lucide-react';

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

interface Props {
  open: boolean;
  onClose: () => void;
  organizations: OrganizationAdminSummary[];
  /** Pre-selected organization */
  organizationId: string | null;
  /** After an invitation was sent, so the list can show it */
  onInvited?: () => void;
}

/** Invite someone into any organization (Queri staff), by email or with a link. */
const InviteUserDialog: React.FC<Props> = ({
  open,
  onClose,
  organizations,
  organizationId: initialOrganizationId,
  onInvited,
}) => {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [hidden, setHidden] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  // Ignore link responses from superseded requests (org changed mid-flight)
  const linkRequestId = useRef(0);

  useEffect(() => {
    if (!open) return;
    setOrganizationId(initialOrganizationId);
    setEmail('');
    setHidden(false);
    setEmailError(null);
    setGeneralError(null);
    setSentTo([]);
    setLink(null);
    setCopied(false);
    linkRequestId.current++;
  }, [open, initialOrganizationId]);

  const selectedOrg = organizations.find((o) => o.id === organizationId);

  const changeOrganization = (id: string) => {
    setOrganizationId(id);
    setSentTo([]);
    // A link is bound to its organization: drop one made for another org
    linkRequestId.current++;
    setLink(null);
    setGeneratingLink(false);
  };

  const sendEmail = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setEmailError(null);
    setGeneralError(null);
    const to = email.trim();
    if (!isValidEmail(to)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setSending(true);
    try {
      // A fresh single-use invitation for this recipient
      const res = await sendInvitationEmail(to, organizationId || undefined, hidden);
      if (!res.success) {
        const fieldError = res.errors?.find((err) => err?.field === 'email');
        if (fieldError?.detail) setEmailError(fieldError.detail);
        else setGeneralError(res.errors?.[0]?.title || 'Failed to send invitation');
        return;
      }
      setSentTo((prev) => [to, ...prev.filter((x) => x !== to)]);
      setEmail('');
      toast.success(`Invitation sent to ${to}`);
      onInvited?.();
    } catch {
      setGeneralError('An error occurred. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const generateLink = async () => {
    const requestId = ++linkRequestId.current;
    setLink(null);
    setCopied(false);
    setGeneratingLink(true);
    try {
      const res = await createInvitation(organizationId || undefined);
      if (requestId !== linkRequestId.current) return;
      if (res.success && res.data) setLink(res.data.inviteUrl);
      else setGeneralError('Failed to generate invitation link');
    } catch {
      if (requestId === linkRequestId.current) {
        setGeneralError('Failed to generate invitation link');
      }
    } finally {
      if (requestId === linkRequestId.current) setGeneratingLink(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="invite-organization">Organization</Label>
            <Select value={organizationId || undefined} onValueChange={changeOrganization}>
              <SelectTrigger id="invite-organization">
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-email">Send email invite</Label>
            <form className="flex gap-2" onSubmit={sendEmail}>
              <Input
                id="invite-email"
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                className={emailError ? 'border-destructive' : ''}
              />
              <Button type="submit" disabled={sending || !organizationId || !isValidEmail(email)}>
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </form>
            {emailError && <p className="text-sm text-destructive">{emailError}</p>}
            <p className="text-xs text-muted-foreground">
              Each person gets their own invitation link. You can send several in a row.
            </p>
            <label className="flex items-start gap-2 pt-1 text-sm">
              <Checkbox
                checked={hidden}
                onCheckedChange={(v) => setHidden(v === true)}
                className="mt-0.5"
              />
              <span>
                Hide from other members
                <span className="block text-xs text-muted-foreground">
                  For Queri test accounts: the account won't appear in member lists.
                </span>
              </span>
            </label>
            {sentTo.length > 0 && (
              <ul className="space-y-1 pt-1" aria-label="Invitations sent">
                {sentTo.map((to) => (
                  <li key={to} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MailCheck className="h-4 w-4 shrink-0 text-green-600" />
                    <span className="truncate">
                      Sent to <span className="text-foreground">{to}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Shareable link: only on request, never for workspaces */}
          {!organizationId ? null : selectedOrg?.isWorkspace ? (
            <p className="text-xs text-muted-foreground">
              {selectedOrg.displayName || selectedOrg.name} is a workspace: members are invited by
              email only.
            </p>
          ) : link || generatingLink ? (
            <div className="space-y-2">
              <Label>Shareable link</Label>
              <div className="flex gap-2">
                <Input
                  value={link || ''}
                  readOnly
                  placeholder={generatingLink ? 'Generating...' : ''}
                  className="font-mono text-sm"
                />
                <Button type="button" variant="outline" size="icon" onClick={copyLink} disabled={!link}>
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={generateLink}
                  disabled={generatingLink}
                  title="Generate a new link for another person"
                  aria-label="Generate a new link for another person"
                >
                  <RefreshCw className={`h-4 w-4 ${generatingLink ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Anyone with this link can join, once, within 48 hours. Generate a new link for each
                additional person.
              </p>
            </div>
          ) : (
            <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={generateLink}>
              Create a shareable link instead
            </Button>
          )}

          {generalError && <p className="text-sm text-destructive">{generalError}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
